import { StarIcon } from '@heroicons/react/24/solid';
import cx from 'classnames';

import { useWebThemeContext } from '~/components/Layout/Context/useWebThemeContext';
import MarkDownRender from '~/components/Markdown/MarkDownRender';
import Typography from '~/components/Typography';

import type { TestimonialsSection } from '~/interfaces/landing_page';

interface Props {
  message: string;
  pictureUrl: string;
  name: string;
  description: string;
  testimonialsSection: TestimonialsSection;
}

const Testimonial = ({ message, pictureUrl, name, description, testimonialsSection }: Props) => {
  const { isEnabled: hasWebThemesEnabled } = useWebThemeContext();

  const renderStarIcon = () => {
    return (
      <StarIcon
        style={{ color: testimonialsSection.card_accent_color }}
        className={cx({
          'h-5 w-5': true,
        })}
      />
    );
  };

  return (
    <div
      style={{
        backgroundColor: testimonialsSection.card_bg_color,
        borderColor: testimonialsSection.card_border_color,
      }}
      className={cx(
        {
          'col-span-1 flex w-full max-w-none flex-col space-y-4 rounded rounded-lg p-8 sm:max-w-xs': true,
          'shadow-sm': !hasWebThemesEnabled && testimonialsSection.card_shadow_size === 'sm',
          'shadow-md': !hasWebThemesEnabled && testimonialsSection.card_shadow_size === 'md',
          'shadow-lg': !hasWebThemesEnabled && testimonialsSection.card_shadow_size === 'lg',
        },
        hasWebThemesEnabled && 'border',
      )}
    >
      {!hasWebThemesEnabled && (
        <div className="flex space-x-1">
          {renderStarIcon()}
          {renderStarIcon()}
          {renderStarIcon()}
          {renderStarIcon()}
          {renderStarIcon()}
        </div>
      )}
      <figure className="flex flex-grow flex-col">
        <blockquote className="flex-grow text-gray-950">
          <Typography
            as="div"
            size="md"
            weight="medium"
            fontFamily={testimonialsSection.card_font_family}
            color={testimonialsSection.card_text_color}
            className={cx({
              'w-full': true,
            })}
          >
            <MarkDownRender>{`“${message}”`}</MarkDownRender>
          </Typography>
        </blockquote>
        <figcaption className="mt-8 flex gap-x-2">
          <img className="h-10 w-10 rounded-full bg-gray-50" src={pictureUrl} alt="" />
          <div className="flex flex-col">
            <Typography
              as="span"
              size="sm"
              weight="bold"
              fontFamily={testimonialsSection.card_font_family}
              color={testimonialsSection.card_text_color}
              className={cx({
                'w-full': true,
              })}
            >
              <MarkDownRender>{name}</MarkDownRender>
            </Typography>
            <Typography
              as="span"
              size="xs"
              weight="light"
              fontFamily={testimonialsSection.card_font_family}
              color={testimonialsSection.card_text_color}
              className={cx({
                'w-full': true,
              })}
            >
              <MarkDownRender>{description}</MarkDownRender>
            </Typography>
          </div>
        </figcaption>
      </figure>
    </div>
  );
};

export default Testimonial;
